<template>
    <div class="container mx-auto">
        <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-gray-200 rounded-md shadow">
                <thead class="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th class="py-3 px-4 text-left text-gray-600 font-semibold">Patient Name</th>
                        <th class="py-3 px-4 text-left text-gray-600 font-semibold">Facility Name</th>
                        <th class="py-3 px-4 text-left text-gray-600 font-semibold">Service</th>
                        <th class="py-3 px-4 text-left text-gray-600 font-semibold">Date Booked</th>
                        <th class="py-3 px-4 text-left text-gray-600 font-semibold">Time Booked</th>
                        <th class="py-3 px-4 text-left text-gray-600 font-semibold">Amount Paid (KES)</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <!-- Show loading row -->
                    <tr v-if="isLoadingBookings">
                        <td colspan="6" class="text-center py-6 text-gray-500">
                            <div class="flex justify-center items-center">
                                <svg class="animate-spin h-5 w-5 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12A8 12 0 018 4a8 8 0 0 1 8 8v0v-8C4 0 0 5.373 0 12h4zm2 5.291A7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3"></path>
                                </svg>
                                <span class="ml-2 text-gray-600">Loading bookings...</span>
                            </div>
                        </td>
                    </tr>
                    <!-- Show empty state when not loading and no bookings -->
                    <tr v-else-if="paginatedBookings.length === 0 && !isLoadingBookings">
                        <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
                            No bookings found.
                        </td>
                    </tr>
                    <!-- Show bookings -->
                    <tr v-for="booking in paginatedBookings" :key="booking.id" class="hover:bg-gray-50">
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {{ booking.patient_name }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {{ booking.facility_name }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {{ booking.service_name }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {{ booking.appointment_date }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {{ formatTime(booking.appointment_time) }}
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {{ formatCurrency(booking.service_cost) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <div class="flex justify-center items-center my-8">
                <div class="flex space-x-2">
                    <button @click="goToPreviousPage" :disabled="currentPage === 1"
                        class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
                        Previous
                    </button>
                    <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{
                        'bg-blue-900 text-white': currentPage === page,
                        'text-gray-600': currentPage !== page
                    }" class="px-3 py-1 rounded hover:bg-blue-100">
                        {{ page }}
                    </button>
                    <span v-if="showEllipsis" class="px-3 py-1 text-gray-600">...</span>
                    <button v-if="showLastPage" @click="goToPage(totalPages)" :class="{
                        'bg-blue-900 text-white': currentPage === totalPages,
                        'text-gray-600': currentPage !== totalPages
                    }" class="px-3 py-1 rounded hover:bg-blue-100">
                        {{ totalPages }}
                    </button>
                    <button @click="goToNextPage" :disabled="currentPage === totalPages"
                        class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
                        Next
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { SpecializedPatientsAPI } from '@/services';
import { ref, computed, watch, onMounted } from 'vue';

const props = defineProps({
    searchQuery: {
        type: String,
        default: '',
    },
});

const currentPage = ref(1);
const bookingsPerPage = ref(10);
const totalPages = ref(1);
const totalItems = ref(0);
const bookings = ref([]);
const showPanel = ref(false);
const selectedBooking = ref(null);
const isLoadingBookings = ref(false); // Add loading state
const errorMessage = ref(null); // Add error message state

const fetchBookings = async (page = 1) => {
    try {
        isLoadingBookings.value = true; // Set loading to true
        errorMessage.value = null; // Clear previous errors
        const response = await SpecializedPatientsAPI.getAllPatientBookings({ page, per_page: bookingsPerPage.value });
        if (response.data && response.data.success) {
            bookings.value = response.data.data;
            totalPages.value = response.data.meta.total_pages || 1;
            totalItems.value = response.data.meta.total_items || 0;
            currentPage.value = response.data.meta.current_page || 1;
        } else {
            errorMessage.value = response.data.message || 'Failed to fetch bookings';
            bookings.value = [];
            totalPages.value = 1;
        }
    } catch (error) {
        console.error('Error fetching bookings:', error);
        errorMessage.value = 'Failed to load bookings. Please try again.';
        bookings.value = [];
        totalPages.value = 1;
    } finally {
        isLoadingBookings.value = false; // Set loading to false
    }
};

onMounted(() => {
    fetchBookings(currentPage.value);
});

watch(currentPage, (newPage) => {
    fetchBookings(newPage);
});

const filteredBookings = computed(() => {
    if (!props.searchQuery) return bookings.value;
    return bookings.value.filter(booking =>
        booking.patient_name.toLowerCase().includes(props.searchQuery.toLowerCase()) ||
        booking.facility_name.toLowerCase().includes(props.searchQuery.toLowerCase()) ||
        booking.service_name.toLowerCase().includes(props.searchQuery.toLowerCase())
    );
});

const paginatedBookings = computed(() => filteredBookings.value);

const visiblePages = computed(() => {
    const pages = [];
    const current = currentPage.value;
    const total = totalPages.value;
    const range = 2;
    for (let i = Math.max(1, current - range); i <= Math.min(total, current + range); i++) {
        pages.push(i);
    }
    return pages;
});

const showEllipsis = computed(() => totalPages.value > 5 && currentPage.value < totalPages.value - 2);
const showLastPage = computed(() => totalPages.value > 5 && currentPage.value < totalPages.value - 2);

watch(() => props.searchQuery, () => {
    currentPage.value = 1;
    fetchBookings(currentPage.value);
});

const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const goToPreviousPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const goToNextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `KES ${Number(amount).toLocaleString('en-KE')}`;
};

const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
        const date = new Date(timeString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch (error) {
        console.error('Error formatting time:', error);
        return 'N/A';
    }
};
</script>